import styled, { css, keyframes } from 'styled-components'
import { NavLink } from 'react-router-dom'
import { rem, opaqueColor, BaseColor, spacing, themeColor, Typography, Color, transitions, ColorShade, borderRadius, link } from '~theme'
import { Icon } from '~components/Icon'
import { InputStyled } from '~components/Form'

export const Container = styled.div`
  position: relative;
  user-select: none;
`

export const PopoverContainer = styled.div<{ zIndex: number; hideOnHiddenReference: boolean }>`
  z-index: ${props => props.zIndex};

  ${props =>
    props.hideOnHiddenReference &&
    css`
      &[data-popper-reference-hidden='true'] {
        visibility: hidden;
        pointer-events: none;
      }
    `}
`

export const TriggerContainer = styled.div`
  flex-shrink: 0;
  width: 100%;
  cursor: pointer;
  user-select: none;

  & > ${InputStyled} {
    width: 100%;

    input,
    textarea {
      width: 100%;
      cursor: pointer;
      user-select: none;
    }
  }
`

const popoverAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
`

export const PopoverContentContainer = styled.div`
  animation: ${popoverAnimation} 0.15s forwards ease-out;
`

export const Content = styled.div`
  min-width: ${rem(148)};
  padding: ${spacing(0.5)};
  border: 1px solid ${themeColor(BaseColor.shade_30)};
  background: ${themeColor(BaseColor.shade_10)};
  border-radius: ${borderRadius};
  max-height: 80vh;
  overflow-y: auto;
`

interface OptionProps {
  isActive?: boolean
  $isAccent?: boolean
  shouldWrap?: boolean
}

const OptionStyles = css<OptionProps>`
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: ${({ shouldWrap }) => (shouldWrap ? 'wrap' : 'nowrap')};
  padding: ${spacing(0.5)};
  border-radius: ${rem(2)};
  cursor: pointer;
  ${Typography.bodyMedium};
  ${Typography.textSemiStrong};

  ${transitions(['color', 'background-color'])};

  & {
    color: ${props => (props.$isAccent ? themeColor(Color.primary, ColorShade.semiStrong) : themeColor(BaseColor.shade_60))};
  }

  &:hover {
    color: ${props => (props.$isAccent ? themeColor(Color.primary, ColorShade.strong) : themeColor(BaseColor.shade_80))};
  }
`

export const Option = styled.div<OptionProps>`
  ${OptionStyles};

  &:hover {
    background-color: ${opaqueColor(themeColor(BaseColor.shade_30))};
  }

  ${({ isActive }) =>
    isActive &&
    css`
      color: ${themeColor(BaseColor.shade_80)};
      background-color: ${themeColor(BaseColor.shade_30)};
    `}
`

export const CheckboxOption = styled.div`
  ${OptionStyles};
`

export const LinkOption = styled(NavLink)`
  ${link};
  ${OptionStyles};
  padding-right: ${spacing()};
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }

  &.active {
    color: ${({ $isAccent }) => ($isAccent ? themeColor(Color.primary, ColorShade.strong) : themeColor(BaseColor.shade_100))};
  }
`

export const OptionIcon = styled(Icon)`
  flex-shrink: 0;
  margin-right: ${spacing(0.5)};
`

export const OptionCaption = styled.span`
  width: 100%;
  margin-top: ${spacing(0.25)};
  ${Typography.bodySmall};
  color: ${themeColor(BaseColor.shade_50)};
`

export const Divider = styled.div`
  height: 1px;
  margin: ${spacing(0.5)} ${spacing(-0.5)};
  background-color: ${themeColor(BaseColor.shade_30)};
`
